import { defineStore } from "pinia";
import { ref, computed, onScopeDispose } from "vue";
import type { ROLocation } from "rainowls-shared";

export interface LocationSearchResult extends ROLocation {
  state?: string;
}

export interface LocationSearchState {
  query: string;
  results: LocationSearchResult[];
  loading: boolean;
  error: string | null;
}

interface GeocodingResult {
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
}

export const useLocationSearchStore = defineStore("location-search", () => {
  const query = ref("");
  const results = ref<LocationSearchResult[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const state = computed<LocationSearchState>(() => ({
    query: query.value,
    results: results.value,
    loading: loading.value,
    error: error.value,
  }));
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let version = 0;

  /** Debounce a search and discard responses for earlier queries. */
  function setQuery(value: string) {
    const request = ++version;
    clearTimeout(timer);
    controller?.abort();
    query.value = value;
    results.value = [];
    error.value = null;
    loading.value = value.trim().length >= 2;
    if (!loading.value) return;

    timer = setTimeout(async () => {
      controller = new AbortController();
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value.trim())}&format=json&limit=5&addressdetails=1`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          throw new Error("Location search is unavailable. Try again.");
        }
        const data: GeocodingResult[] = await response.json();
        if (request !== version) return;
        results.value = data.flatMap((result) => {
          const lat = Number(result.lat);
          const lng = Number(result.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];

          const name =
            result.address?.city ||
            result.address?.town ||
            result.address?.village ||
            result.display_name.split(",")[0];
          return [
            {
              id: `${lat}-${lng}`,
              lat,
              lng,
              timeZone: "",
              name,
              city: name,
              country: result.address?.country || "",
              state: result.address?.state || "",
            },
          ];
        });
      } catch (cause) {
        if (request === version) {
          error.value =
            cause instanceof Error ? cause.message : "Location search failed";
        }
      } finally {
        if (request === version) {
          loading.value = false;
        }
      }
    }, 300);
  }

  /** Cancel the active search and clear its presentation state. */
  function clear() {
    setQuery("");
  }

  onScopeDispose(clear);

  return { state, setQuery, clear };
});
